import { useEffect, useRef } from 'react'

/**
 * Dismisses an open popup on Escape or a mousedown outside it.
 *
 * `containers` is a ref, or an array of refs, covering every part of the popup. A popup that
 * renders through `Portal` sits outside its trigger's markup, so both the trigger and the
 * portalled panel have to count as inside or choosing an item would dismiss the popup on
 * mousedown, before the click could reach it.
 *
 * `onDismiss` is told which one it was, as 'escape' or 'outside'. Escape leaves the user at the
 * keyboard, so a popup that moved focus should hand it back; an outside click has already put
 * focus somewhere deliberate, and taking it away would fight the user.
 *
 * Document listeners are attached only while the popup is open.
 */
export const usePopupDismiss = (containers, onDismiss, isOpen) => {
  // Keep the latest callback and refs without reattaching the document listeners.
  const dismissRef = useRef(onDismiss)
  const containersRef = useRef(containers)

  useEffect(() => {
    dismissRef.current = onDismiss
    containersRef.current = containers
  })

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const onMouseDown = e => {
      const refs = [].concat(containersRef.current)
      if (refs.some(ref => ref.current?.contains(e.target))) {
        return
      }
      dismissRef.current('outside')
    }

    const onKeyDown = e => {
      if (e.key === 'Escape') {
        dismissRef.current('escape')
      }
    }

    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])
}
